import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Wallet,
  Check,
  ChartPie,
  Bell,
  FileArrowDown,
  Shield,
  ArrowRight,
  Star,
  Lightning,
  Users,
  CloudArrowUp,
  ShoppingCart,
  CreditCard
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const features = [
  {
    icon: ChartPie,
    title: "Gráficos por Categoria",
    description: "Visualize seus gastos organizados por categorias com cores personalizadas."
  },
  {
    icon: Bell,
    title: "Alertas de Vencimento",
    description: "Nunca mais perca uma data. Receba avisos de contas próximas do vencimento."
  },
  {
    icon: FileArrowDown,
    title: "Exportação Fácil",
    description: "Exporte relatórios em Excel ou PDF com um clique."
  },
  {
    icon: CloudArrowUp,
    title: "Backup Automático",
    description: "Seus dados são salvos automaticamente a cada 5 minutos."
  },
  {
    icon: Shield,
    title: "Dados Seguros",
    description: "Cada usuário tem seus dados isolados e protegidos."
  }
];

const testimonials = [
  {
    name: "Maria Silva",
    role: "Empresária",
    text: "Finalmente consigo controlar todas as contas da empresa sem perder vencimentos!",
    rating: 5
  },
  {
    name: "João Santos",
    role: "Contador",
    text: "Os relatórios detalhados economizam horas do meu trabalho toda semana.",
    rating: 5
  },
  {
    name: "Ana Costa",
    role: "Autônoma",
    text: "Os gráficos me ajudam a entender para onde vai meu dinheiro.",
    rating: 5
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post("/checkout/create-session", { origin_url: originUrl });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Erro ao iniciar pagamento. Tente novamente.");
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary-foreground" weight="fill" />
              </div>
              <span className="font-heading font-extrabold text-xl tracking-tight text-primary">
                Financeiro
              </span>
              <Badge variant="secondary" className="text-xs">SaaS</Badge>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                data-testid="login-nav-button"
              >
                Entrar
              </Button>
              <Button 
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="tracing-beam"
                data-testid="signup-nav-button"
              >
                <ShoppingCart size={16} className="mr-1" />
                {checkoutLoading ? "Aguarde..." : "Comprar"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-6 animate-fade-in">
              <Lightning size={14} className="mr-1" />
              Sistema completo de gestão financeira
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-in">
              Controle suas <span className="text-primary">contas a pagar</span> com inteligência
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto animate-fade-in">
              Organize, acompanhe e nunca mais perca um vencimento. 
              Com gráficos por categoria, relatórios detalhados e backup automático.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
              <Button 
                size="lg" 
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="tracing-beam text-lg px-8 h-14"
                data-testid="hero-cta-button"
              >
                <CreditCard size={22} className="mr-2" />
                {checkoutLoading ? "Aguarde..." : "Comprar por R$ 29,90"}
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/login')}
                className="text-lg px-8 h-14"
                data-testid="hero-login-button"
              >
                Já tenho conta
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Pagamento único. Acesso vitalício. Sem mensalidades.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/30" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Tudo que você precisa
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Recursos poderosos para simplificar sua gestão financeira
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={idx} 
                  className={`card-hover stagger-item cursor-pointer transition-all duration-300 ${isHovered === idx ? 'border-primary shadow-lg' : ''}`}
                  onMouseEnter={() => setIsHovered(idx)}
                  onMouseLeave={() => setIsHovered(null)}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors ${isHovered === idx ? 'bg-primary' : 'bg-primary/10'}`}>
                      <Icon size={24} className={isHovered === idx ? 'text-primary-foreground' : 'text-primary'} weight="fill" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Como funciona
            </h2>
            <p className="text-lg text-muted-foreground">
              Em 3 passos simples você organiza suas finanças
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Cadastre suas contas", desc: "Adicione suas contas a pagar de forma rápida e organizada" },
              { step: "2", title: "Organize por categorias", desc: "Crie categorias com cores para visualizar seus gastos" },
              { step: "3", title: "Acompanhe e pague", desc: "Receba alertas e marque parcelas como pagas" }
            ].map((item, idx) => (
              <div key={idx} className="text-center stagger-item">
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              O que nossos usuários dizem
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="stagger-item">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-500" weight="fill" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users size={18} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{testimonial.name}</p>
                      <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
            Pronto para organizar suas finanças?
          </h2>
          <p className="text-lg text-muted-foreground mb-4">
            Pagamento único de apenas <span className="text-primary font-bold text-2xl">R$ 29,90</span>
          </p>
          <p className="text-muted-foreground mb-10">
            Acesso vitalício. Sem mensalidades. Sem limites.
          </p>
          <Button 
            size="lg" 
            onClick={handleCheckout}
            disabled={checkoutLoading}
            className="tracing-beam text-lg px-12 h-14"
            data-testid="cta-buy-button"
          >
            <ShoppingCart size={22} className="mr-2" />
            {checkoutLoading ? "Aguarde..." : "Comprar Agora"}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Wallet className="w-4 h-4 text-primary-foreground" weight="fill" />
              </div>
              <span className="font-heading font-bold text-primary">Financeiro</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 Financeiro SaaS. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
